import { Divider, Flex, Form, Select, Table } from 'antd';
import { useContext, useEffect, useMemo, useState } from 'react';
import { apiBasePath, REFRESH_INTERVAL, REFRESH_INTERVAL_OPTIONS } from '../../utils/constants';
import { GlobalContext } from '../../utils/globalContext';
import dayjs from 'dayjs';
import { CustomDatePicker } from '../common';
import axios from 'axios';
import locale from 'antd/es/date-picker/locale/es_ES';
import { normalizeInvoiceData } from './normalize';
import { ActionColumn, ErrorDescriptionColumn, StatusColumn } from '../common/tableCustomColumns';
import fileDownload from 'js-file-download';
import { useThrottledAsync } from '../../hooks/useThrottledAsync';

export const Invoice = () => {
  const today = dayjs();
  const isFirstWeek = today.date() <= 5;
  let downloadLock = false;

  const defaultValuesDates = {
    fromDate: isFirstWeek ? dayjs().subtract(1, 'month').startOf('month') : dayjs().startOf('month'),
    toDate: today
  };
  const { user } = useContext(GlobalContext);
  const [loader, setLoader] = useState(false);
  const [loaderDownload, setLoaderDownload] = useState({});
  const [invoiceList, setInvoiceList] = useState([]);
  const [columns, setColumns] = useState([]);
  const [dateFilter, setDateFilter] = useState(defaultValuesDates);
  const [refreshInterval, setRefreshInterval] = useState(REFRESH_INTERVAL);
  const [filteredInfo, setFilteredInfo] = useState({});

  const getListInvoice = async () => {
    setLoader(true);
    try {
      const responseInvoiceList = await axios.get(
        `${apiBasePath}/invoice/${user.clie_id}/from/${dateFilter.fromDate.format('YYYY-MM-DD')}/to/${dateFilter.toDate.format('YYYY-MM-DD')}`
      );
      const dataInvoices = responseInvoiceList.data.listInvoice;
      const { dataSource, columns } = normalizeInvoiceData(dataInvoices);

      setInvoiceList(dataSource);
      setColumns(columns);
    } catch (e) {
      console.error('🚀 ~ getListInvoice ~ e:', e);
    } finally {
      setLoader(false);
    }
  };
  const downLoadFile = async (record, xtencion) => {
    if (downloadLock) return;
    downloadLock = true;
    setLoaderDownload({ ...loaderDownload, [record.oper_id]: true });

    try {
      const idOper = record.oper_id;
      const response = await fetch(`${apiBasePath}/invoice/download-file/oper_id/${idOper}/discriminator/${xtencion}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/octet-stream'
        }
      });
      if (!response.ok) {
        throw new Error(`Error: ${response.statusText}`);
      }

      const blob = await response.blob();
      const filename = `Factura_${idOper}.${xtencion}`;

      fileDownload(blob, filename);
    } catch (error) {
      console.error('Error downloading the PDF:', error.response);
    } finally {
      setTimeout(() => {
        downloadLock = false;
        setLoaderDownload({ ...loaderDownload, [record.oper_id]: false });
      }, 3000);
    }
  };

  const retrieCorrelation = async () => {
    try {
      await axios.get(`${apiBasePath}/invoice/correlation-id/clie_id/${user.clie_id}`);
    } catch (error) {
      console.error('Error downloading the PDF:', error.response);
    }
  };

  const [handleRefresh, isRefreshing] = useThrottledAsync(async () => {
    await Promise.all([retrieCorrelation(), getListInvoice()]);
  }, 3000);

  useEffect(() => {
    getListInvoice();
  }, [dateFilter.toDate]);

  useEffect(() => {
    if (refreshInterval > 0) {
      const intervalId = setInterval(() => {
        handleRefresh();
      }, refreshInterval);
      return () => clearInterval(intervalId);
    }
  }, [refreshInterval, handleRefresh]);

  const columnsTable = useMemo(() => {
    return [
      ...(columns ?? []),
      StatusColumn(filteredInfo.status || null),
      ErrorDescriptionColumn('-lg'),
      ActionColumn({ handleCustomAction: downLoadFile, loader: loaderDownload })
    ];
  }, [columns, loaderDownload, filteredInfo]);

  const handleChange = (filters) => {
    setFilteredInfo(filters);
  };

  return (
    <div className="basic-layout">
      <Flex justify="space-between" align="center" className="basic-layout-header">
        <h1>Mis Facturas</h1>
        <Flex gap="small" justify="flex-end" align="center">
          <Form.Item name="dateFromTo" label="Fechas" layout="vertical">
            <CustomDatePicker
              picker="week"
              locale={locale}
              defaultValue={[dateFilter.fromDate, dateFilter.toDate]}
              format="DD-MM-YYYY"
              onChange={(value) => {
                if (value) {
                  setDateFilter({ fromDate: value[0], toDate: value[1] });
                }
              }}
              onRefresh={handleRefresh}
              loading={loader || isRefreshing}
            />
          </Form.Item>
          <Form.Item name="refresh_interval" label="Refrescar" layout="vertical" style={{ minWidth: '80px' }}>
            <Select
              defaultValue={REFRESH_INTERVAL}
              onChange={(value) => setRefreshInterval(value)}
              options={REFRESH_INTERVAL_OPTIONS}
            />
          </Form.Item>
        </Flex>
      </Flex>
      <Divider />
      <Table dataSource={invoiceList} columns={columnsTable} loading={loader} onChange={handleChange} />
    </div>
  );
};
